/**
 * Diagnostic Utilities
 *
 * Tools for debugging API errors and database issues in production
 */

import { supabase } from '@/integrations/supabase/client';
import { prodLogger } from '@/utils/productionLogger';
import { logger } from '@/utils/logger';

/**
 * Test subsidy_feedback insert with full error details
 * Run this in browser console: window.testSubsidyFeedback()
 */
export async function testSubsidyFeedback() {
  prodLogger.debug('🔍 Testing subsidy_feedback insert...\n');

  try {
    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser();

    if (authError || !user) {
      logger.error('❌ Not authenticated:', authError);
      return { success: false, error: 'Not authenticated' };
    }

    prodLogger.debug('✅ Authenticated as:', user.id);

    // Try to insert a test feedback record
    const testFeedback = {
      user_id: user.id,
      profile_id: '313f4d69-58bd-4fa5-aa19-7b55049bce8d', // Your test profile
      subsidy_id: '08958832-b5d8-47bd-b52a-995ab34389e4', // Test subsidy ID
      feedback_type: 'interested',
      subsidy_sectors: ['Test'],
      subsidy_region: 'National',
      subsidy_organism: 'Test Organism',
      subsidy_amount_range: '10k-50k',
      subsidy_funding_type: 'Subvention'
    };

    prodLogger.debug('📤 Attempting insert with data:', testFeedback);

    const { data, error } = await supabase
      .from('subsidy_feedback')
      .upsert(testFeedback, {
        onConflict: 'user_id,profile_id,subsidy_id'
      })
      .select()
      .single();

    if (error) {
      logger.error('❌ INSERT FAILED', new Error('❌ INSERT FAILED'));
      console.error('Error Code:', error.code);
      console.error('Error Message:', error.message);
      console.error('Error Details:', error.details);
      console.error('Error Hint:', error.hint);
      logger.error('Full Error Object:', error);
      return { success: false, error };
    }

    prodLogger.debug('✅ INSERT SUCCEEDED');
    prodLogger.debug('Inserted data:', data);
    return { success: true, data };

  } catch (err: any) {
    logger.error('❌ EXCEPTION:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Check subsidy_feedback RLS policies
 */
export async function checkRLSPolicies() {
  prodLogger.debug('🔍 Checking RLS policies...\n');

  try {
    const { data: { user } } = await supabase.auth.getUser();
    prodLogger.debug('Current user:', user?.id);

    // Try to read from subsidy_feedback
    const { data, error } = await supabase
      .from('subsidy_feedback')
      .select('*')
      .limit(5);

    if (error) {
      console.error('❌ SELECT failed:', error.message);
    } else {
      prodLogger.debug('✅ SELECT works, fetched', data.length, 'records');
    }

  } catch (err: any) {
    console.error('❌ Exception:', err.message);
  }
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).testSubsidyFeedback = testSubsidyFeedback;
  (window as any).checkRLSPolicies = checkRLSPolicies;
  prodLogger.debug('🛠️  Diagnostic tools loaded!');
  prodLogger.debug('   Run: window.testSubsidyFeedback()');
  prodLogger.debug('   Run: window.checkRLSPolicies()');
}
